import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import SeoHead from '@/components/base/SeoHead';
import JsonLd from '@/components/base/JsonLd';
import { milestones, reviews, contactInfo } from '@/mocks/about';

export default function About() {
  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '韩漫网',
    url: 'https://www.hanmanwang.com',
    description: '韩漫网——领先的中文韩漫阅读平台，提供免费下拉式韩漫阅读体验，资源全面，日更不断。',
    foundingDate: '2018',
    contactPoint: {
      '@type': 'ContactPoint',
      email: 'contact@hanmanwang.com',
      contactType: 'customer service',
      availableLanguage: ['Chinese'],
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.hanmanwang.com' },
      { '@type': 'ListItem', position: 2, name: '关于我们', item: 'https://www.hanmanwang.com/about' },
    ],
  };

  return (
    <>
      <SeoHead
        title="关于我们_韩漫网品牌介绍与联系方式"
        description="韩漫网品牌介绍——成立于2018年，是领先的中文韩漫阅读平台。了解韩漫网的发展历程、用户口碑与联系方式。提供免费下拉式韩漫阅读体验，资源全面，日更不断。"
        keywords="韩漫网,关于我们,韩漫,漫画品牌,韩漫平台,联系韩漫网"
        canonicalUrl="https://www.hanmanwang.com/about"
      />
      <JsonLd data={organizationSchema} />
      <JsonLd data={breadcrumbSchema} />

      <ScrollToTop />
      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-24 pb-16">
          <div className="px-4 md:px-6 max-w-7xl mx-auto">
            <Breadcrumb items={[{ label: '首页', href: '/' }, { label: '关于我们' }]} />
          </div>

          {/* Brand Intro */}
          <section className="mt-6 px-4 md:px-6 max-w-7xl mx-auto">
            <h1 className="text-3xl md:text-4xl font-bold text-foreground-950 text-center" style={{ fontFamily: 'var(--font-heading)' }}>
              关于韩漫网
            </h1>
            <p className="text-foreground-500 text-center mt-4 max-w-2xl mx-auto leading-relaxed">
              韩漫网成立于2018年，是领先的中文韩漫阅读平台。我们致力于为全球华语读者提供最全面、最优质的韩国漫画阅读体验。
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mt-10">
              {brandStats.map((stat) => (
                <div key={stat.label} className="text-center p-8 rounded-xl bg-background-50 border border-background-200">
                  <div className="text-3xl md:text-4xl font-bold text-primary-500" style={{ fontFamily: 'var(--font-heading)' }}>
                    {stat.value}
                  </div>
                  <p className="text-sm text-foreground-500 mt-2">{stat.label}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Mission */}
          <section className="mt-16 py-14 bg-background-100">
            <div className="px-4 md:px-6 max-w-3xl mx-auto text-center">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950" style={{ fontFamily: 'var(--font-heading)' }}>
                我们的使命
              </h2>
              <p className="mt-4 text-foreground-600 leading-relaxed">
                韩漫网自创立以来，始终秉持"让每一个漫画爱好者都能轻松获取优质内容"的理念。我们与多家韩国漫画平台及独立画师合作，为读者提供正版授权的韩漫资源。通过不断优化阅读体验，我们打造了流畅的下拉式阅读模式，让读者能够沉浸在漫画世界中，享受无缝的阅读乐趣。
              </p>
            </div>
          </section>

          {/* Milestones */}
          <section className="mt-16 px-4 md:px-6 max-w-4xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 text-center" style={{ fontFamily: 'var(--font-heading)' }}>
              发展历程
            </h2>
            <div className="mt-10 relative">
              <div className="absolute left-5 md:left-1/2 top-0 bottom-0 w-px bg-background-200 md:-translate-x-px"></div>
              <div className="space-y-8">
                {milestones.map((m, i) => (
                  <div key={m.year} className={`relative flex flex-col md:flex-row gap-4 md:gap-8 ${i % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                    <div className="md:w-1/2 flex items-start gap-4 md:justify-end">
                      <div className={`${i % 2 === 0 ? '' : 'md:order-2'} bg-background-50 border border-background-200 rounded-xl p-5 flex-1 md:max-w-sm`}>
                        <span className="text-sm font-bold text-primary-500">{m.year}</span>
                        <h3 className="text-base font-semibold text-foreground-900 mt-1">{m.title}</h3>
                        <p className="text-sm text-foreground-500 mt-2 leading-relaxed">{m.description}</p>
                      </div>
                    </div>
                    <div className="hidden md:flex w-0 justify-center">
                      <div className="w-3 h-3 rounded-full bg-primary-500 border-2 border-background-50 relative z-10"></div>
                    </div>
                    <div className="md:w-1/2"></div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Reviews */}
          <section className="mt-16 py-14 bg-background-100">
            <div className="px-4 md:px-6 max-w-7xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 text-center" style={{ fontFamily: 'var(--font-heading)' }}>
                用户口碑
              </h2>
              <p className="text-foreground-500 text-center mt-2">听听读者们怎么说</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 mt-8">
                {reviews.map((review) => (
                  <div key={review.id} className="bg-background-50 border border-background-200 rounded-xl p-5">
                    <div className="flex items-center gap-3 mb-3">
                      <img
                        src={review.avatar}
                        alt={review.name}
                        className="w-10 h-10 rounded-full object-cover"
                      />
                      <div>
                        <p className="text-sm font-medium text-foreground-900">{review.name}</p>
                        <div className="flex gap-0.5 mt-0.5">
                          {Array.from({ length: 5 }).map((_, i) => (
                            <div key={i} className="w-3 h-3 flex items-center justify-center">
                              <i className={`text-xs ${i < review.rating ? 'ri-star-fill text-yellow-400' : 'ri-star-fill text-background-200'}`}></i>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                    <p className="text-sm text-foreground-600 leading-relaxed">{review.content}</p>
                    <p className="text-xs text-foreground-400 mt-3">{review.date}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Contact */}
          <section className="mt-16 px-4 md:px-6 max-w-3xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 text-center" style={{ fontFamily: 'var(--font-heading)' }}>
              联系我们
            </h2>
            <p className="text-foreground-500 text-center mt-2">如有任何问题或合作意向，欢迎与我们取得联系</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-8">
              {contactInfo.map((info) => (
                <div key={info.type} className="flex items-center gap-4 p-5 rounded-xl bg-background-50 border border-background-200">
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 flex-shrink-0">
                    <i className={`${info.icon} text-xl`}></i>
                  </div>
                  <div>
                    <p className="text-xs text-foreground-400">{info.label}</p>
                    <p className="text-sm font-medium text-foreground-900 mt-0.5">{info.value}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}

const brandStats = [
  { value: '5000+', label: '漫画资源' },
  { value: '500万+', label: '注册用户' },
  { value: '200+', label: '合作画师' },
];